import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TowerChainage } from '../types/timeLocationChartTypes';

/** Capas base disponibles. Esri es satelital; OpenTopoMap muestra curvas de nivel. */
export type BaseLayerType = 'SATELLITE' | 'TOPOGRAPHIC';

const BASE_LAYERS: Record<BaseLayerType, { url: string; attribution: string; label: string }> = {
    SATELLITE: {
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
        label: 'Satelital'
    },
    TOPOGRAPHIC: {
        url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
        attribution: 'Map data: &copy; OpenStreetMap contributors, SRTM | Style: &copy; OpenTopoMap (CC-BY-SA)',
        label: 'Topográfico'
    }
};

interface TimeLocationMapProps {
    towers: TowerChainage[];
    /** Torre resaltada, normalmente la que el cursor está tocando en el diagrama. */
    highlightedTowerNumber?: string | null;
    baseLayer: BaseLayerType;
    onBaseLayerChange: (layer: BaseLayerType) => void;
    height?: number;
}

/**
 * Ajusta el encuadre del mapa al trazado completo cada vez que cambian las torres.
 * Va como componente hijo porque el hook useMap solo funciona dentro del MapContainer.
 */
const FitToRoute: React.FC<{ bounds: LatLngBoundsExpression | null }> = ({ bounds }) => {
    const map = useMap();
    React.useEffect(() => {
        if (bounds) {
            map.fitBounds(bounds, { padding: [20, 20] });
        }
    }, [bounds, map]);
    return null;
};

/**
 * Avisa a Leaflet que su contenedor cambió de tamaño.
 *
 * Leaflet calcula el encuadre una sola vez y no observa el alto del contenedor, así que al
 * mover el control de alto las teselas quedan cortadas o desplazadas hasta que se le pide
 * recalcular.
 */
const ResizeOnHeightChange: React.FC<{ height: number }> = ({ height }) => {
    const map = useMap();
    React.useEffect(() => {
        map.invalidateSize();
    }, [height, map]);
    return null;
};

/**
 * Mapa del trazado de la línea, como banda superior del Diagrama Espacio-Tiempo.
 *
 * No es el fondo del diagrama y no puede serlo: el diagrama tiene kilometraje y fechas en sus
 * ejes, mientras que el mapa trabaja en latitud y longitud. Son proyecciones distintas. Lo que
 * sí comparten es el trazado, así que ambos se leen en conjunto resaltando la misma torre.
 */
const TimeLocationMap: React.FC<TimeLocationMapProps> = ({
    towers,
    highlightedTowerNumber,
    baseLayer,
    onBaseLayerChange,
    height = 200
}) => {
    // Solo las torres con coordenada importada del KMZ se pueden dibujar.
    const located = useMemo(
        () => towers.filter(t => t.latitude !== null && t.longitude !== null),
        [towers]
    );

    const routePositions = useMemo<LatLngExpression[]>(
        () => located.map(t => [t.latitude as number, t.longitude as number]),
        [located]
    );

    const bounds = useMemo<LatLngBoundsExpression | null>(() => {
        if (routePositions.length < 2) return null;
        return routePositions as LatLngBoundsExpression;
    }, [routePositions]);

    if (located.length === 0) {
        return (
            <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                    Este proyecto no tiene coordenadas cargadas. Importe el archivo KMZ para ver el trazado en el mapa.
                </Typography>
            </Box>
        );
    }

    const layer = BASE_LAYERS[baseLayer];

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Typography variant="caption" color="textSecondary">
                    Trazado de la línea ({located.length} torres localizadas)
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={baseLayer}
                    onChange={(_, value) => value && onBaseLayerChange(value)}
                >
                    <ToggleButton value="SATELLITE">Satelital</ToggleButton>
                    <ToggleButton value="TOPOGRAPHIC">Topográfico</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* preferCanvas dibuja el trazado y los puntos sobre un canvas en vez de un SVG.
                Es lo que permite exportar el mapa correctamente: html2canvas rehace los SVG
                serializandolos y reescribiendo su ancho y alto, lo que descuadra el viewBox
                desplazado que usa Leaflet y deja los puntos corridos respecto de las teselas.
                Un canvas, en cambio, se copia pixel a pixel y cae donde corresponde. */}
            <MapContainer
                preferCanvas
                style={{ height, width: '100%' }}
                center={routePositions[0] as LatLngExpression}
                zoom={11}
                scrollWheelZoom={false}
            >
                {/* La key fuerza el recambio de teselas al cambiar de capa base.
                    crossOrigin es lo que permite exportar el mapa: sin el atributo, el navegador
                    guarda las teselas como recurso opaco y html2canvas no las puede leer, con lo
                    que el mapa sale gris en la imagen exportada. Ambos proveedores responden con
                    Access-Control-Allow-Origin, asi que la peticion con CORS no falla. */}
                <TileLayer
                    key={baseLayer}
                    url={layer.url}
                    attribution={layer.attribution}
                    crossOrigin="anonymous"
                />

                <FitToRoute bounds={bounds} />
                <ResizeOnHeightChange height={height} />

                <Polyline positions={routePositions} pathOptions={{ color: '#d32f2f', weight: 3 }} />

                {located.map(tower => {
                    const highlighted = tower.towerNumber === highlightedTowerNumber;
                    return (
                        <CircleMarker
                            key={tower.towerId}
                            center={[tower.latitude as number, tower.longitude as number]}
                            radius={highlighted ? 8 : 4}
                            pathOptions={{
                                color: highlighted ? '#ffffff' : '#004F86',
                                fillColor: highlighted ? '#ed6c02' : '#004F86',
                                fillOpacity: 1,
                                weight: highlighted ? 2 : 1
                            }}
                        >
                            <LeafletTooltip>
                                Torre {tower.towerNumber} — km {tower.chainageKm}
                            </LeafletTooltip>
                        </CircleMarker>
                    );
                })}
            </MapContainer>
        </Box>
    );
};

export default TimeLocationMap;
